/*
  pd-product.js · gallery, tiered buy box, subscription toggle and mobile sticky add-to-cart
  for sections/pd-product.liquid. Vanilla JS, no dependencies, loaded with `defer` by that section.

  Every price shown is computed from data Liquid wrote into each tier card (data-pd-tier JSON):
    { id, title, short, available, price, units, singles, days, pos, group, media, plans: { <planId>: { p: firstPrice, r: renewalPrice } } }
  singles = the 1-pack price of the same size x units (the "buying singles" comparison). Nothing is invented client-side.
  Adding to cart is handled by pd-theme.js (form[data-pd-add-form]); without it the form posts normally.
*/
(function () {
  'use strict';

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function money(cents) {
    return window.pdFormatMoney ? window.pdFormatMoney(cents) : '$' + (cents / 100).toFixed(2);
  }
  function fill(template, map) {
    return Object.keys(map).reduce(function (out, key) { return out.split('[' + key + ']').join(map[key]); }, template || '');
  }

  /* ---------- <pd-gallery> ---------- */
  class PdGallery extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.track = this.querySelector('[data-pd-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-pd-slide]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-pd-thumb]'));
      this.index = 0;
      this.target = null;
      this.thumbs.forEach(function (thumb, i) { thumb.addEventListener('click', function () { self.go(i); }); });
      this.querySelectorAll('[data-pd-step]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.go(self.index + Number(btn.getAttribute('data-pd-step'))); });
      });
      // A swipe or a wheel is the visitor driving: hand the active index straight back to the observer.
      ['pointerdown', 'touchstart', 'wheel'].forEach(function (evt) {
        self.track.addEventListener(evt, function () { self.unlock(); }, { passive: true });
      });
      if (this.track && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var n = self.slides.indexOf(entry.target);
            // A smooth scroll crosses every slide on the way, and the observer reports each one. While a
            // programmatic jump is in flight, ignore the slides it passes, or the thumbnail strip chases the
            // whole way there.
            if (self.target !== null && n !== self.target) return;
            if (self.target !== null && n === self.target) self.unlock();
            self.setActive(n);
          });
        }, { root: this.track, threshold: 0.6 });
        this.slides.forEach(function (slide) { self.io.observe(slide); });
      }
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); clearTimeout(this.unlockTimer); }
    unlock() { this.target = null; clearTimeout(this.unlockTimer); }
    go(i) {
      if (!this.track || !this.slides.length) return;
      var self = this;
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      this.target = i;
      clearTimeout(this.unlockTimer);
      // Fallback for browsers that never fire the settling intersection (reduced motion, instant scroll).
      this.unlockTimer = setTimeout(function () { self.target = null; }, 900);
      this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    setActive(i) {
      if (i < 0) return;
      this.index = i;
      this.thumbs.forEach(function (thumb, n) {
        thumb.classList.toggle('is-active', n === i);
        thumb.setAttribute('aria-current', n === i ? 'true' : 'false');
      });
      // Keep the active thumbnail visible in the strip (horizontal scroll of the strip only, never the page).
      var t = this.thumbs[i];
      var strip = t && t.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        var left = Math.max(0, t.offsetLeft - (strip.clientWidth - t.offsetWidth) / 2);
        if (Math.abs(strip.scrollLeft - left) > 2) strip.scrollTo({ left: left, behavior: reducedMotion ? 'auto' : 'smooth' });
      }
      this.slides.forEach(function (slide, n) {
        if (n !== i) slide.querySelectorAll('video').forEach(function (v) { v.pause(); });
      });
    }
    goToMedia(mediaId) {
      var i = this.slides.findIndex(function (s) { return s.getAttribute('data-media-id') === String(mediaId); });
      if (i > -1) this.go(i);
    }
  }

  /* ---------- <pd-buy-box> ---------- */
  class PdBuyBox extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.sectionId = this.getAttribute('data-section');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-pd-tier]'));
      this.optInputs = Array.prototype.slice.call(this.querySelectorAll('input[data-pd-opt]'));
      this.modes = Array.prototype.slice.call(this.querySelectorAll('input[data-pd-mode]'));
      this.planSelect = this.querySelector('[data-pd-plan-select]');
      this.planInput = this.querySelector('[data-pd-plan-input]');
      this.terms = this.querySelector('[data-pd-terms]');
      this.modeSave = this.querySelector('[data-pd-mode-save]');
      this.atcButtons = Array.prototype.slice.call(this.querySelectorAll('[data-pd-atc]'));
      this.sticky = this.querySelector('[data-pd-sticky]');
      this.freeShip = parseInt(this.getAttribute('data-free-ship') || '0', 10) || 0;
      this.updateUrl = this.getAttribute('data-update-url') !== 'false';
      this.labels = {
        save: this.getAttribute('data-label-save') || 'Save [amount] vs [units] singles',
        perDay: this.getAttribute('data-label-perday') || '[amount]/day',
        add: this.getAttribute('data-label-add') || 'Add to cart',
        soldOut: this.getAttribute('data-label-soldout') || 'Sold out',
        subSave: this.getAttribute('data-label-subsave') || 'Save [pct]'
      };
      this.cards.forEach(function (card) {
        try { card._d = JSON.parse(card.getAttribute('data-pd-tier')); } catch (e) { card._d = { plans: {} }; }
        var input = card.querySelector('input[type="radio"]');
        if (input) input.addEventListener('change', function () { self.update(true); });
      });
      this.optInputs.forEach(function (input) {
        input.addEventListener('change', function () { if (input.checked) self.onGroup(self.groupKey()); });
      });
      this.modes.forEach(function (input) { input.addEventListener('change', function () { self.update(false); }); });
      if (this.planSelect) this.planSelect.addEventListener('change', function () { self.update(false); });
      // An option tapped before this script ran (slow network), or restored by the browser on back/forward, must win:
      // re-sync the visible tier cards to the checked colour and size so the cart never gets another one.
      var key = this.optInputs.length ? this.groupKey() : '';
      var sel = this.selectedCard();
      if (key && (!sel || String(sel._d.group) !== key || sel.hidden)) {
        this.onGroup(key);
      } else {
        this.update(false);
      }
      this.initSticky();
    }
    disconnectedCallback() {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      document.documentElement.classList.remove('pd-has-sticky');
    }
    selectedCard() {
      return this.cards.find(function (c) { var i = c.querySelector('input'); return i && i.checked; });
    }
    isSubscribe() {
      if (this.getAttribute('data-requires-plan') === 'true') return true;
      var mode = this.modes.find(function (m) { return m.checked; });
      return !!mode && mode.value === 'subscribe';
    }
    // The variant group Liquid wrote on every tier card: the checked value of each non-Pack option, in option
    // order, joined the same way. With one option it is that value on its own.
    groupKey() {
      var slots = [];
      this.optInputs.forEach(function (input) {
        if (input.checked) slots[Number(input.getAttribute('data-pd-opt')) || 0] = input.value;
      });
      return slots.filter(function (v) { return v !== undefined; }).join(' ~|~ ');
    }
    onGroup(group) {
      var current = this.selectedCard();
      var pos = current ? current._d.pos : 1;
      var target = null;
      this.cards.forEach(function (card) {
        var show = String(card._d.group) === String(group);
        card.hidden = !show;
        if (show && !target && card._d.pos === pos && card._d.available) target = card;
      });
      if (!target) target = this.cards.find(function (c) { return !c.hidden && c._d.available; }) || this.cards.find(function (c) { return !c.hidden; });
      if (target) target.querySelector('input').checked = true;
      this.update(true);
    }
    update(fromUser) {
      var self = this;
      var sub = this.isSubscribe();
      var planId = sub && this.planSelect ? this.planSelect.value : '';
      if (this.planInput) {
        // Disabled when buying one-time, so no empty selling_plan parameter is posted to /cart/add.
        this.planInput.value = sub ? planId : '';
        this.planInput.disabled = !sub;
      }
      var selected = null;

      this.cards.forEach(function (card) {
        var d = card._d;
        var input = card.querySelector('input');
        var plan = sub && d.plans ? d.plans[planId] : null;
        var price = plan ? plan.p : d.price;
        var checked = !!(input && input.checked);
        card.classList.toggle('is-selected', checked);
        if (checked) selected = card;

        var priceEl = card.querySelector('[data-pd-card-price]');
        if (priceEl) priceEl.textContent = money(price);
        var wasEl = card.querySelector('[data-pd-card-was]');
        if (wasEl) {
          var showWas = !!plan && plan.p < d.price;
          wasEl.hidden = !showWas;
          if (showWas) wasEl.textContent = money(d.price);
        }
        var shipEl = card.querySelector('[data-pd-card-ship]');
        if (shipEl && self.freeShip > 0) shipEl.hidden = price < self.freeShip;
        var perDayEl = card.querySelector('[data-pd-card-perday]');
        if (perDayEl) {
          perDayEl.hidden = !(d.days > 0);
          if (d.days > 0) perDayEl.textContent = fill(self.labels.perDay, { amount: money(Math.round(price / d.days)) });
        }
        var saveEl = card.querySelector('[data-pd-card-save]');
        if (saveEl) {
          var saved = d.singles - price;
          var showSave = d.units > 1 && d.singles > 0 && saved > 0;
          saveEl.hidden = !showSave;
          if (showSave) {
            saveEl.textContent = fill(self.labels.save, {
              amount: money(saved),
              pct: Math.floor((saved * 100) / d.singles) + '%',
              units: d.units
            });
          }
        }
      });

      if (!selected) return;
      var d = selected._d;
      var plan = sub && d.plans ? d.plans[planId] : null;
      var price = plan ? plan.p : d.price;
      // A variant that is not part of the chosen plan is sold one-time (never post a plan it cannot take).
      if (this.planInput && sub && !plan) { this.planInput.value = ''; this.planInput.disabled = true; }

      this.atcButtons.forEach(function (btn) {
        btn.disabled = !d.available;
        var label = btn.querySelector('[data-pd-atc-label]');
        if (label) label.textContent = d.available ? self.labels.add : self.labels.soldOut;
        var priceEl = btn.querySelector('[data-pd-atc-price]');
        if (priceEl) { priceEl.textContent = d.available ? money(price) : ''; priceEl.hidden = !d.available; }
      });
      // The mobile price line in the title block sits outside <pd-buy-box>, so it is updated by scope
      // (its own .pd-product__info) and never by a document-wide query: a page can hold two buy boxes.
      var priceScope = this.closest('.pd-product__info') || this;
      priceScope.querySelectorAll('[data-pd-price-live]').forEach(function (el) { el.textContent = money(price); });
      var stickyName = this.querySelector('[data-pd-sticky-variant]');
      if (stickyName) stickyName.textContent = d.short || d.title;

      if (this.modeSave) {
        var firstPlan = d.plans ? d.plans[this.planSelect ? this.planSelect.value : ''] : null;
        var pct = firstPlan && d.price > 0 ? Math.floor(((d.price - firstPlan.p) * 100) / d.price) : 0;
        this.modeSave.hidden = !(pct > 0);
        if (pct > 0) this.modeSave.textContent = fill(this.labels.subSave, { pct: pct + '%' });
      }

      if (this.terms) {
        this.terms.hidden = !plan;
        if (plan) {
          var option = this.planSelect ? this.planSelect.options[this.planSelect.selectedIndex] : null;
          var tpl = plan.r !== plan.p ? this.terms.getAttribute('data-template') : this.terms.getAttribute('data-template-same');
          this.terms.textContent = fill(tpl, {
            plan: option ? option.text.trim() : '',
            first: money(plan.p),
            renewal: money(plan.r)
          });
        }
      }

      if (fromUser) {
        if (this.updateUrl) {
          try {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', d.id);
            window.history.replaceState(window.history.state, '', url.toString());
          } catch (e) { /* ignore */ }
        }
        if (d.media) {
          var gallery = document.querySelector('pd-gallery[data-section="' + this.sectionId + '"]');
          if (gallery && gallery.goToMedia) gallery.goToMedia(d.media);
        }
      }
      this.dispatchEvent(new CustomEvent('pd:variant:change', { bubbles: true, detail: d }));
    }
    initSticky() {
      // Show the mobile bar once the main button has scrolled out above the viewport.
      // A rAF-throttled passive scroll check (one getBoundingClientRect per frame) also catches
      // instant jumps such as "back to top", which an IntersectionObserver can miss.
      var main = this.querySelector('[data-pd-atc-main]');
      if (!this.sticky || !main) return;
      var sticky = this.sticky;
      var ticking = false;
      function check() {
        ticking = false;
        var past = main.getBoundingClientRect().bottom < 0;
        if (sticky.hidden === !past) return;
        sticky.hidden = !past;
        document.documentElement.classList.toggle('pd-has-sticky', past);
      }
      this.onScroll = function () {
        if (!ticking) { ticking = true; window.requestAnimationFrame(check); }
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      check();
    }
  }

  function define(name, ctor) { if (!customElements.get(name)) customElements.define(name, ctor); }
  define('pd-gallery', PdGallery);
  define('pd-buy-box', PdBuyBox);
})();
